import { datadogLogs } from "@datadog/browser-logs";
import useNaverADsScript, { NaverADsType } from "@repo/hooks/lib/useNaverADsScript";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";

import useLayout from "@hooks/reducerHooks/useLayout";
import useUser from "@hooks/reducerHooks/useUser";

import { KAKAO_REDIRECT_URL } from "@const/constants";
import { URLS } from "@const/urls";

import { NOTIFICATIONS } from "@utils/notifications";
import { generateRandomString } from "@utils/string";

import { fetchMetaData, fetchSocialLogin } from "@api/lamdaApi";

interface IProps {
  onComplete?: (...args: any) => void;
  onError?: () => void;
}

declare global {
  interface Window {
    Kakao: any;
  }
}

const KAKAO_KEY = import.meta.env.VITE_REACT_APP_KAKAO_LOGIN_KEY;

const useSocialLogin = ({ onComplete, onError }: IProps = {}) => {
  const { openModal, loginModalProps, openToast, closeLoginModal } = useLayout();
  const { fetchUser, createCustomer, createToken } = useUser();
  const { tryAppendNaverADsScript } = useNaverADsScript();
  const navigate = useNavigate();

  const [isProgressing, setIsProgressing] = useState(false); // 소셜로그인 lambda API 진행중
  const [isLoading, setIsLoading] = useState(false); // 로그인 진행중

  const socialLogin = useCallback(
    async (url: string) => {
      if (isProgressing) return;

      setIsProgressing(true);

      try {
        const response = await fetchSocialLogin(url);

        if (response) {
          if (!response.isCustomer) {
            /** 회원 가입 */
            const randomPassword = generateRandomString(8);
            const { data } = await createCustomer({
              variables: { input: { email: response.email, password: randomPassword } },
            });

            const customerId = data?.customerCreate.customer?.id || "";

            /** 쇼피파이 메타 데이터 업데이트 람다 함수 */
            await fetchMetaData({ email: response.email, customerId, password: randomPassword, param: url });

            /**  로그인 */
            await createToken({ variables: { input: { email: response.email, password: randomPassword } } }).then(
              async ({ data }) => {
                if (data?.customerAccessTokenCreate.customerUserErrors.length) {
                  return;
                }

                const customerAccessToken = data?.customerAccessTokenCreate.customerAccessToken?.accessToken;
                await fetchUser({
                  variables: { customerAccessToken },
                }).then(({ data: customerData }) => {
                  onComplete?.(customerAccessToken, customerData?.customer);
                });
              },
            );

            tryAppendNaverADsScript({ type: NaverADsType.Join, value: "0" });
            openToast(NOTIFICATIONS.SUCCESS_REGISTER);
          } else if (response.isCustomer && response.password) {
            /**
             * createToken 함수의 onCompleted 콜백에서 처리해주는 로직과
             * 이 부분의 then async await 처리된 부분이 동일. 중복으로 호출되고 있음. (L.60도 마찬가지)
             * 제거 필요 + 추후 리팩토링
             */
            /** SNS 유저 로그인 */
            await createToken({ variables: { input: { email: response.email, password: response.password } } }).then(
              async ({ data }) => {
                if (data?.customerAccessTokenCreate.customerUserErrors.length) {
                  return;
                }

                const customerAccessToken = data?.customerAccessTokenCreate.customerAccessToken?.accessToken;
                await fetchUser({
                  variables: { customerAccessToken: data?.customerAccessTokenCreate.customerAccessToken?.accessToken },
                }).then(({ data: customerData }) => {
                  onComplete?.(customerAccessToken, customerData?.customer);
                });
              },
            );
          } else {
            /** 이메일 가입 유저 */
            openToast(NOTIFICATIONS.WARN_EMAIL_ACCOUNT);
            navigate(URLS.HOME, { replace: true });
          }
        }
      } catch (error) {
        openToast(NOTIFICATIONS.ERROR_DEFAULT);
        onError?.();

        const message = error instanceof Error ? error.message : String(error);
        datadogLogs.logger.error("Something went wrong", { message });
        throw new Error(`Something went wrong : ${message}`);
      } finally {
        setIsProgressing(false);
      }
    },
    [isProgressing, openModal, onError],
  );

  const kakaoInit = useCallback(async () => {
    if (!window.Kakao) return;

    const kakao = window.Kakao;
    if (!kakao.isInitialized()) {
      await kakao.init(KAKAO_KEY);
    }

    return kakao;
  }, []);

  const kakaoLogin = useCallback(async () => {
    if (isProgressing || isLoading) return;

    setIsLoading(true);
    const kakao = await kakaoInit();
    if (!kakao) return;

    closeLoginModal();
    sessionStorage.setItem(KAKAO_REDIRECT_URL, JSON.stringify(loginModalProps.redirectUrl));
    kakao.Auth.authorize({ redirectUri: window.location.origin + URLS.KAKAO_OAUTH });
    setIsLoading(false);
  }, [kakaoInit, isProgressing, isLoading, setIsLoading, loginModalProps.redirectUrl]);

  return {
    isProgressing,
    kakaoInit,
    kakaoLogin,
    socialLogin,
    isLoading,
    setIsLoading,
  };
};

export default useSocialLogin;
